import { useState, useEffect } from "react";
import { useModalStore } from "../../../stores/modalStore.js";
import { useChannelStore } from "../../../stores/channelStore.js";

const STORE_KEY = 'aethex_bookmarks';

export function useBookmarks() {
  const load = () => { try { return JSON.parse(localStorage.getItem(STORE_KEY) || '[]'); } catch { return []; } };
  const save = (list) => localStorage.setItem(STORE_KEY, JSON.stringify(list));
  return {
    getAll: load,
    add: (msg) => { const list = load(); if (!list.find(m => m.id === msg.id)) { list.unshift({ ...msg, savedAt: new Date().toISOString() }); save(list.slice(0, 100)); } },
    remove: (id) => { save(load().filter(m => m.id !== id)); },
    has: (id) => !!load().find(m => m.id === id),
  };
}

export function BookmarksModal() {
  const { isOpen, type, onClose } = useModalStore();
  const setCurrentChannel = useChannelStore(s => s.setCurrentChannel);

  const [bookmarks, setBookmarks] = useState([]);
  const [search,    setSearch]    = useState('');

  useEffect(() => {
    if (isOpen && type === 'bookmarks') {
      try { setBookmarks(JSON.parse(localStorage.getItem(STORE_KEY) || '[]')); } catch { setBookmarks([]); }
    }
  }, [isOpen, type]);

  if (!isOpen || type !== 'bookmarks') return null;

  const remove = (id) => {
    const next = bookmarks.filter(m => m.id !== id);
    setBookmarks(next);
    localStorage.setItem(STORE_KEY, JSON.stringify(next));
  };

  const jumpTo = (bm) => {
    if (bm.channelId) setCurrentChannel(bm.channelId);
    onClose();
    setTimeout(() => {
      const el = document.getElementById(`msg-${bm.id}`);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.classList.add('msg-highlight'); setTimeout(() => el.classList.remove('msg-highlight'), 2000); }
    }, 400);
  };

  const filtered = bookmarks.filter(bm => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (bm.content || bm.text || '').toLowerCase().includes(q) || (bm.author || bm.senderName || '').toLowerCase().includes(q);
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={onClose}>
      <div style={{ width: '100%', maxWidth: 560, maxHeight: '85vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary,#13141f)', borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,0.6)', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>🔖 Saved Messages</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{bookmarks.length} bookmarked</div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 18, cursor: 'pointer' }}>✕</button>
          </div>
          {bookmarks.length > 0 && (
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search bookmarks…"
              style={{ width: '100%', padding: '7px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '48px 0', textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🔖</div>
              <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)' }}>
                {bookmarks.length === 0 ? 'No bookmarks yet' : 'No bookmarks match'}
              </div>
              {bookmarks.length === 0 && (
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)', marginTop: 6 }}>Right-click any message and select "Bookmark"</div>
              )}
            </div>
          ) : (
            filtered.map(bm => (
              <div key={bm.id} style={{ padding: '12px', marginBottom: 6, borderRadius: 10, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#06b6d4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff' }}>
                      {(bm.author || bm.senderName || '?')[0]?.toUpperCase()}
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{bm.author || bm.senderName}</span>
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>{bm.savedAt ? new Date(bm.savedAt).toLocaleDateString() : ''}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => jumpTo(bm)}
                      style={{ padding: '3px 10px', borderRadius: 6, background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', color: '#a5b4fc', fontSize: 11, cursor: 'pointer' }}>
                      Jump →
                    </button>
                    <button onClick={() => remove(bm.id)}
                      style={{ padding: '3px 8px', borderRadius: 6, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#fca5a5', fontSize: 11, cursor: 'pointer' }}>
                      ✕
                    </button>
                  </div>
                </div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5, paddingLeft: 36 }}>
                  {(bm.content || bm.text || '').slice(0, 200)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
